<template>
    <!-- Panel de administración (/admin). Mismo lenguaje que Mi cuenta y Admin →
         Usuarios (bkCuenta.css + cuentaMenu.css): lateral con título en degradé,
         cifras y áreas; a la derecha las secciones agrupadas por área.
         Las secciones salen de components/Admin/modules.json (campo `grupo`) y
         se filtran por los permisos de cada admin. El orden es personal: se
         guarda en `usuario.admin_menu_order` (keys de secciones y `grupo:<k>`). -->
    <div v-if="isAdmin" class="ad-page">
        <div class="bk ad">
            <aside class="bk-side ad-side">
                <h1 class="bk-title">{{ $t('admin_menu.title_a') }} <span class="bk-grad">{{ $t('admin_menu.title_b') }}</span></h1>
                <p class="bk-subtitle">{{ $t('admin_menu.sub') }}</p>

                <div class="ad-stats">
                    <div class="ad-stat"><span class="ad-stat-n">{{ totalSecciones }}</span><span class="ad-stat-k">{{ $t('admin_menu.stat.sections') }}</span></div>
                    <div class="ad-stat"><span class="ad-stat-n">{{ gruposTodos.length }}</span><span class="ad-stat-k">{{ $t('admin_menu.stat.areas') }}</span></div>
                </div>

                <nav v-if="gruposTodos.length > 1" class="bk-snav ad-nav" :aria-label="$t('admin_menu.nav_label')">
                    <p class="bk-snav-title">{{ $t('admin_menu.nav') }}</p>
                    <ul class="bk-snav-list">
                        <li v-for="g in gruposTodos" :key="g.k">
                            <a :href="`#ad-g-${g.k}`" :class="['bk-snav-link', { 'is-active': activo === g.k }]" :aria-current="activo === g.k ? 'true' : null" @click.prevent="irA(g.k)">
                                <span aria-hidden="true" v-html="g.ic"></span>
                                <span class="ad-nav-t">{{ $t(`admin_menu.g.${g.k}.t`) }}</span>
                                <span class="ad-nav-n">{{ g.mods.length }}</span>
                            </a>
                        </li>
                    </ul>
                </nav>
            </aside>

            <section class="ad-main" aria-labelledby="ad-main-title">
                <h2 id="ad-main-title" class="ad-sr">{{ $t('admin_menu.title_a') }} {{ $t('admin_menu.title_b') }}</h2>

                <div v-if="totalSecciones" class="ad-toolbar">
                    <label class="ad-search">
                        <span aria-hidden="true" v-html="IC.search"></span>
                        <span class="ad-sr">{{ $t('admin_menu.search_label') }}</span>
                        <input ref="buscar" v-model="q" type="search" autocomplete="off" :placeholder="$t('admin_menu.search_ph')" :disabled="ordenando" @keydown.esc="q = ''" />
                    </label>
                    <button type="button" :class="['bk-btn', ordenando ? 'bk-btn--primary' : 'bk-btn--ghost', 'ad-order-btn']" :aria-pressed="String(ordenando)" @click="alternarOrden">
                        <span aria-hidden="true" v-html="ordenando ? IC.check : IC.sort"></span>{{ $t(ordenando ? 'admin_menu.order_done' : 'admin_menu.order') }}
                    </button>
                </div>

                <div v-if="ordenando" class="ad-hint" role="status">
                    <span aria-hidden="true" v-html="IC.info"></span>
                    <p>{{ $t('admin_menu.order_hint') }}</p>
                    <button v-if="ordenPersonal" type="button" class="ad-link" @click="restablecer">{{ $t('admin_menu.reset') }}</button>
                </div>

                <p v-if="q && grupos.length" class="ad-count" aria-live="polite">{{ $tc('admin_menu.results', resultados, { n: resultados }) }}</p>

                <!-- Sin secciones (ningún permiso) -->
                <div v-if="!totalSecciones" class="ad-empty">
                    <span aria-hidden="true" v-html="IC.lock"></span>
                    <h3>{{ $t('admin_menu.none_t') }}</h3>
                    <p>{{ $t('admin_menu.none_p') }}</p>
                </div>
                <!-- Búsqueda sin resultados -->
                <div v-else-if="!grupos.length" class="ad-empty" aria-live="polite">
                    <span aria-hidden="true" v-html="IC.search"></span>
                    <h3>{{ $t('admin_menu.empty_t', { q }) }}</h3>
                    <p>{{ $t('admin_menu.empty_p') }}</p>
                    <button type="button" class="bk-btn bk-btn--ghost" @click="limpiar">{{ $t('admin_menu.clear') }}</button>
                </div>

                <section
                    v-for="(g, gi) in grupos"
                    :id="`ad-g-${g.k}`"
                    :key="g.k"
                    :class="['ad-grp', { 'is-ordering': ordenando }]"
                    :aria-labelledby="`ad-gt-${g.k}`"
                >
                    <header class="ad-grp-head">
                        <span class="ad-grp-ic" aria-hidden="true" v-html="g.ic"></span>
                        <div class="ad-grp-txt">
                            <h3 :id="`ad-gt-${g.k}`" class="ad-grp-t">{{ $t(`admin_menu.g.${g.k}.t`) }}</h3>
                            <p class="ad-grp-d">{{ $t(`admin_menu.g.${g.k}.d`) }}</p>
                        </div>
                        <div v-if="ordenando && grupos.length > 1" class="ad-arrows">
                            <button type="button" class="ad-arrow" :disabled="gi === 0" :aria-label="$t('admin_menu.up', { n: $t(`admin_menu.g.${g.k}.t`) })" @click="moverGrupo(g.k, -1)"><span aria-hidden="true" v-html="IC.up"></span></button>
                            <button type="button" class="ad-arrow" :disabled="gi === grupos.length - 1" :aria-label="$t('admin_menu.down', { n: $t(`admin_menu.g.${g.k}.t`) })" @click="moverGrupo(g.k, 1)"><span aria-hidden="true" v-html="IC.down"></span></button>
                        </div>
                    </header>

                    <ul class="ad-tiles">
                        <li v-for="(m, mi) in g.mods" :key="m.key" class="ad-tile-li">
                            <!-- Ordenando: la tarjeta deja de ser link para no entrar sin querer. -->
                            <div v-if="ordenando" class="ad-tile is-static">
                                <span class="ad-tile-ic" aria-hidden="true" v-html="iconoDe(m)"></span>
                                <span class="ad-tile-txt">
                                    <span class="ad-tile-t">{{ titulo(m) }}<em v-if="m.superAdminOnly" class="ad-badge">{{ $t('admin_menu.super') }}</em></span>
                                    <span class="ad-tile-d">{{ descripcion(m) }}</span>
                                </span>
                                <span class="ad-arrows">
                                    <button type="button" class="ad-arrow" :disabled="mi === 0" :aria-label="$t('admin_menu.up', { n: titulo(m) })" @click="moverSeccion(g, mi, -1)"><span aria-hidden="true" v-html="IC.up"></span></button>
                                    <button type="button" class="ad-arrow" :disabled="mi === g.mods.length - 1" :aria-label="$t('admin_menu.down', { n: titulo(m) })" @click="moverSeccion(g, mi, 1)"><span aria-hidden="true" v-html="IC.down"></span></button>
                                </span>
                            </div>
                            <router-link v-else :to="m.url" class="ad-tile" :aria-label="$t('admin_menu.open', { n: titulo(m) })">
                                <span class="ad-tile-ic" aria-hidden="true" v-html="iconoDe(m)"></span>
                                <span class="ad-tile-txt">
                                    <span class="ad-tile-t">{{ titulo(m) }}<em v-if="m.superAdminOnly" class="ad-badge">{{ $t('admin_menu.super') }}</em></span>
                                    <span class="ad-tile-d">{{ descripcion(m) }}</span>
                                </span>
                                <span class="ad-tile-go" aria-hidden="true" v-html="IC.chev"></span>
                            </router-link>
                        </li>
                    </ul>
                </section>
            </section>
        </div>

        <transition name="ad-toast">
            <div v-if="aviso" :class="['ad-toast', `ad-toast--${aviso.tipo}`]" :role="aviso.tipo === 'error' ? 'alert' : 'status'">
                <span aria-hidden="true" v-html="aviso.tipo === 'error' ? IC.warn : IC.check"></span>{{ aviso.txt }}
            </div>
        </transition>
    </div>
</template>

<script>
import modules from '@/components/Admin/modules.json';
import ICON from '@/components/Cuenta/iconos';
import { mapGetters } from 'vuex';

// Íconos: texto fijo de este archivo, nunca datos del usuario.
const P = d => `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${d}</svg>`;
const IC = {
    search: ICON.search, lock: ICON.lock,
    chev: P('<path d="m9 18 6-6-6-6"/>'),
    up: P('<path d="m6 15 6-6 6 6"/>'),
    down: P('<path d="m6 9 6 6 6-6"/>'),
    sort: P('<path d="M7 4v16M3.5 7.5 7 4l3.5 3.5M17 20V4M13.5 16.5 17 20l3.5-3.5"/>'),
    check: P('<path d="M20 6 9 17l-5-5"/>'),
    info: P('<circle cx="12" cy="12" r="9"/><path d="M12 11v5M12 8h.01"/>'),
    warn: P('<path d="M12 3 2 20h20z"/><path d="M12 10v4M12 17h.01"/>'),
};

// Áreas en su orden por defecto. Una sección sin `grupo` (o con uno que no
// está acá) cae en "otros", así un módulo nuevo nunca desaparece del menú.
const GRUPOS = [
    { k: 'inventario', ic: ICON.building },
    { k: 'reservas', ic: ICON.cal },
    { k: 'clientes', ic: ICON.msg },
    // Megáfono: lo que traen los partners y las campañas.
    { k: 'marketing', ic: P('<path d="M3.5 10v4a1.5 1.5 0 0 0 1.5 1.5h2l5.5 4V4.5L7 8.5H5A1.5 1.5 0 0 0 3.5 10Z"/><path d="M16 8.5a5 5 0 0 1 0 7M18.8 5.8a9 9 0 0 1 0 12.4"/>') },
    { k: 'usuarios', ic: ICON.user },
    { k: 'otros', ic: P('<rect x="4" y="4" width="6.5" height="6.5" rx="1.5"/><rect x="13.5" y="4" width="6.5" height="6.5" rx="1.5"/><rect x="4" y="13.5" width="6.5" height="6.5" rx="1.5"/><rect x="13.5" y="13.5" width="6.5" height="6.5" rx="1.5"/>') },
];
const GRUPO_KEYS = GRUPOS.map(g => g.k);
const PREF = 'grupo:';
// Orden por defecto de las secciones dentro de cada área (lo más usado
// primero). Una sección que no está acá va al final, en el orden de modules.json.
const POR_DEFECTO = ['backoffice', 'oferta-disponible', 'carga-masiva', 'property', 'informes', 'reservations', 'coupon',
    'dashboard-intakes', 'mensajeria', 'marketing-partners', 'crm-anfitriones', 'usuarios', 'generic-link', 'modelos', 'permissions', 'tableros'];

const ICONOS = {
    backoffice: P('<rect x="3" y="4" width="18" height="16" rx="2.5"/><path d="M3 9.5h18M9 9.5V20"/>'),
    // Barras con un globo: oferta disponible por mes y por país.
    'oferta-disponible': P('<path d="M4 20h16"/><path d="M6.5 16v-4M10.5 16V9M14.5 16v-6"/><circle cx="18.5" cy="6.5" r="2.5"/><path d="M16 6.5h5M18.5 4c.8.7 1.2 1.6 1.2 2.5S19.3 8.3 18.5 9"/>'),
    'carga-masiva': ICON.upload,
    property: ICON.building,
    informes: ICON.report,
    reservations: ICON.cal,
    coupon: P('<path d="M3 8.5V7a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v1.5a2.5 2.5 0 0 0 0 5V17a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-3.5a2.5 2.5 0 0 0 0-5Z"/><path d="M14.5 5v14" stroke-dasharray="2 2.5"/>'),
    'dashboard-intakes': P('<path d="M9 6h11M9 12h11M9 18h11"/><circle cx="4.5" cy="6" r="1"/><circle cx="4.5" cy="12" r="1"/><circle cx="4.5" cy="18" r="1"/>'),
    mensajeria: ICON.msg,
    usuarios: P('<circle cx="9" cy="8" r="3.5"/><path d="M2.5 20a6.5 6.5 0 0 1 13 0"/><path d="M16 4.6a3.5 3.5 0 0 1 0 6.8M18 14.2a6.5 6.5 0 0 1 3.5 5.8"/>'),
    'generic-link': ICON.link,
    modelos: ICON.sliders,
    permissions: ICON.admin,
    // Apretón de manos: partners.
    'marketing-partners': P('<path d="m11 17 2 2a1.4 1.4 0 0 0 2-2"/><path d="m14 14 2.5 2.5a1.4 1.4 0 0 0 2-2l-3.9-3.9a2.1 2.1 0 0 0-3 0l-.9.9a1.4 1.4 0 0 1-2-2l2.8-2.8a3.6 3.6 0 0 1 4.6-.4l.4.3a3 3 0 0 0 2.1.6H21v7"/><path d="M3 4.5h1.5a3 3 0 0 1 2.1.9L8 6.8M3 4.5v8.3l5.5 5.5a1.4 1.4 0 0 0 2-2"/>'),
    // Tablero con columnas: prospectos de anfitriones (CRM).
    'crm-anfitriones': P('<rect x="3" y="4" width="18" height="16" rx="2.5"/><path d="M9 4v16M15 4v16"/><path d="M5.5 8h1.5M11 8h2M17 8h1.5M11 11.5h2"/>'),
    // Columnas de distinto alto: tableros personales.
    tableros: P('<rect x="3" y="4" width="5" height="16" rx="1.5"/><rect x="10" y="4" width="5" height="11" rx="1.5"/><rect x="17" y="4" width="4" height="7" rx="1.5"/>'),
};

// Para buscar sin acentos ni mayúsculas.
const norm = s => String(s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();

export default {
    name: 'AdminPanel',
    data() {
        return {
            IC,
            q: '',
            ordenando: false,
            activo: null,
            aviso: null,
        };
    },
    computed: {
        ...mapGetters(['isAdmin', 'isSuperAdmin', 'hasAdminPermission', 'adminMenuOrder']),
        // Secciones que el admin puede ver: el tilde manda para todos y
        // superAdminOnly suma el requisito de rol (igual que antes).
        visibles() {
            return modules.filter(m => !(m.superAdminOnly && !this.isSuperAdmin) && this.hasAdminPermission(m.key));
        },
        orden() { return Array.isArray(this.adminMenuOrder) ? this.adminMenuOrder : []; },
        ordenPersonal() { return this.orden.length > 0; },
        totalSecciones() { return this.visibles.length; },
        // Áreas con sus secciones en el orden personal (las keys que no están en
        // el orden guardado van después, en el orden por defecto).
        gruposTodos() {
            const rank = (k, def) => { const i = this.orden.indexOf(k); return i === -1 ? 1e6 + def : i; };
            const porGrupo = {};
            this.visibles.forEach((m, i) => {
                const k = GRUPO_KEYS.includes(m.grupo) ? m.grupo : 'otros';
                const def = POR_DEFECTO.indexOf(m.key);
                (porGrupo[k] = porGrupo[k] || []).push({ m, r: rank(m.key, def === -1 ? 100 + i : def) });
            });
            return GRUPOS
                .map((g, i) => ({ ...g, r: rank(PREF + g.k, i), mods: (porGrupo[g.k] || []).sort((a, b) => a.r - b.r).map(x => x.m) }))
                .filter(g => g.mods.length)
                .sort((a, b) => a.r - b.r);
        },
        // Lo que se muestra: con búsqueda, solo las secciones que coinciden
        // (por nombre, descripción o nombre del área).
        grupos() {
            const t = norm(this.q);
            if (!t || this.ordenando) return this.gruposTodos;
            return this.gruposTodos
                .map(g => {
                    const enArea = norm(this.$t(`admin_menu.g.${g.k}.t`)).includes(t);
                    return { ...g, mods: enArea ? g.mods : g.mods.filter(m => norm(`${this.titulo(m)} ${this.descripcion(m)} ${m.key}`).includes(t)) };
                })
                .filter(g => g.mods.length);
        },
        resultados() { return this.grupos.reduce((n, g) => n + g.mods.length, 0); },
    },
    created() {
        if (!this.isAdmin) this.$router.push('/');
    },
    mounted() {
        this.observar();
    },
    beforeDestroy() {
        if (this._obs) this._obs.disconnect();
        clearTimeout(this._avisoT);
    },
    watch: {
        grupos() { this.$nextTick(this.observar); },
    },
    methods: {
        titulo(m) {
            const k = `admin_menu.m.${m.key}.t`;
            return this.$te(k) ? this.$t(k) : this.$t(m.title);
        },
        descripcion(m) {
            const k = `admin_menu.m.${m.key}.d`;
            return this.$te(k) ? this.$t(k) : '';
        },
        iconoDe(m) { return ICONOS[m.key] || GRUPOS[GRUPOS.length - 1].ic; },
        limpiar() {
            this.q = '';
            this.$nextTick(() => { if (this.$refs.buscar) this.$refs.buscar.focus(); });
        },
        irA(k) {
            const el = document.getElementById(`ad-g-${k}`);
            if (!el) return;
            this.activo = k;
            el.scrollIntoView({ behavior: 'smooth', block: 'start' });
            const h = el.querySelector('h3');
            if (h) { h.setAttribute('tabindex', '-1'); h.focus({ preventScroll: true }); }
        },
        // Marca en el lateral el área que se está viendo.
        observar() {
            if (this._obs) this._obs.disconnect();
            if (typeof IntersectionObserver === 'undefined') return;
            this._obs = new IntersectionObserver(entradas => {
                const vis = entradas.filter(e => e.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
                if (vis[0]) this.activo = vis[0].target.id.replace('ad-g-', '');
            }, { rootMargin: '-120px 0px -55% 0px' });
            this.$el.querySelectorAll && this.$el.querySelectorAll('.ad-grp').forEach(s => this._obs.observe(s));
        },

        // ── Orden personal ───────────────────────────────────────────────
        alternarOrden() {
            this.ordenando = !this.ordenando;
            if (this.ordenando) this.q = '';
        },
        // Orden completo a guardar: áreas primero (grupo:<k>) y después las
        // secciones, cada una en el orden que se ve. Las keys guardadas de
        // secciones que este admin no ve se conservan al final (otro admin
        // puede compartir la cuenta de permisos más adelante).
        ordenActual(grupos) {
            const keys = grupos.map(g => PREF + g.k).concat(...grupos.map(g => g.mods.map(m => m.key)));
            return keys.concat(this.orden.filter(k => !keys.includes(k)));
        },
        moverGrupo(k, d) {
            const gs = this.gruposTodos.slice();
            const i = gs.findIndex(g => g.k === k), j = i + d;
            if (i < 0 || j < 0 || j >= gs.length) return;
            [gs[i], gs[j]] = [gs[j], gs[i]];
            this.guardar(this.ordenActual(gs), `ad-g-${k}`, d);
        },
        moverSeccion(g, i, d) {
            const j = i + d;
            if (j < 0 || j >= g.mods.length) return;
            const gs = this.gruposTodos.map(x => (x.k === g.k ? { ...x, mods: x.mods.slice() } : x));
            const mods = gs.find(x => x.k === g.k).mods;
            [mods[i], mods[j]] = [mods[j], mods[i]];
            this.guardar(this.ordenActual(gs), null, d, g.k, j);
        },
        restablecer() { this.guardar([]); },
        // Optimista: el store lo deja en localStorage y la API lo guarda en la cuenta.
        guardar(orden, idGrupo, d, gk, j) {
            this.$store.dispatch('saveAdminMenuOrder', orden)
                .then(() => this.avisar(this.$t('admin_menu.saved'), 'ok'))
                .catch(err => {
                    console.error('[Admin] No se pudo guardar el orden del menú:', err);
                    this.avisar(this.$t('admin_menu.error'), 'error');
                });
            // El foco sigue al elemento que se movió (si llegó a un borde, a la otra flecha).
            this.$nextTick(() => {
                let cont = null;
                if (idGrupo) cont = document.getElementById(idGrupo);
                else if (gk) { const s = document.getElementById(`ad-g-${gk}`); cont = s && s.querySelectorAll('.ad-tile-li')[j]; }
                if (!cont) return;
                const flechas = cont.querySelectorAll(idGrupo ? '.ad-grp-head .ad-arrow' : '.ad-arrow');
                const b = flechas[d < 0 ? 0 : 1];
                (b && !b.disabled ? b : flechas[d < 0 ? 1 : 0]).focus();
            });
        },
        avisar(txt, tipo) {
            clearTimeout(this._avisoT);
            this.aviso = { txt, tipo };
            this._avisoT = setTimeout(() => { this.aviso = null; }, 2600);
        },
    },
};
</script>

<style src="../../components/BankInformation/bkCuenta.css"></style>
<style src="../../components/Cuenta/cuentaMenu.css"></style>
<style src="../../components/Admin/adminMenu.css"></style>
